<template>
  <main class="w-full">
    <header class="mb-2 py-2 px-6 bg-white sticky top-0">
      <h1 class="text-3xl mb-2 font-bold text-gray-800">Modèles de produit</h1>
      
      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="relative flex-1">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input placeholder="Rechercher un modèle..."
            class="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :value="searchTerm" @input="searchTerm = $event.target.value" />
        </div>
        <button @click="openAddModal"
          class="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md transition">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Nouveau modèle
        </button>
      </div>
    </header>
    
    <div class="p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <p class="text-gray-600">Chargement des modèles...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredproducts.length === 0 && !loading" class="bg-white rounded-xl shadow border border-gray-200">
        <div class="p-12 text-center">
          <svg class="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">
            {{ searchTerm ? 'Aucun résultat' : 'Aucun modèle de produit' }}
          </h3>
          <p class="text-gray-600 mb-6">
            {{ searchTerm ? 'Aucun modèle ne correspond à votre recherche' : 'Commencez par créer votre premier modèle' }}
          </p>
          <button @click="openAddModal"
            class="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md transition mx-auto">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nouveau modèle
          </button>
        </div>
      </div>

      <!-- Product Models Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="product in filteredproducts" :key="product.id"
          class="bg-white rounded-xl shadow border border-gray-200 hover:shadow-lg transition-shadow">
          <div class="p-5">
            <div class="flex items-start justify-between mb-3">
              <div>
                <h3 class="font-semibold text-gray-800">{{ product.name }}</h3>
                <div v-if="getBrandName(product.brand_id)" class="text-sm text-gray-600 mt-1">
                  {{ getBrandName(product.brand_id) }}
                </div>
              </div>
              <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status)}`">
                {{ getStatusLabel(product.status) }}
              </span>
            </div>

            <p v-if="product.description" class="text-sm text-gray-600 mb-3 line-clamp-2">
              {{ product.description }}
            </p>

            <div class="flex items-center justify-between pt-3 border-t border-gray-200">
              <div class="flex space-x-2">
                <button @click="handleEdit(product)"
                  class="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition">
                  <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  Modifier
                </button>
                <button @click="showDeleteModal = { id: product.id, name: product.name }"
                  class="p-2 border border-gray-300 rounded-lg hover:bg-red-50 transition">
                  <svg class="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Product Model Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition"
        @click.self="resetForm">
        <div class="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-lg font-semibold text-gray-800">
                {{ editingproduct ? 'Modifier le modèle' : 'Nouveau modèle' }}
              </h3>
              <button @click="resetForm" class="p-2 hover:bg-gray-100 rounded-lg transition">
                <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form @submit.prevent="handleSubmit" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Nom du modèle *
                </label>
                <input v-model="productform.name" required
                   @input="handleNameChange"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Marque *
                </label>
                <select v-model="productform.brand_id"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Sélectionner la marque</option>
                  <option v-for="brand in brands" :value="brand.id" :key="brand.id">{{ brand.name }}</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie *
                </label>
                <select v-model="productform.categorie_id"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Sélectionner la catégorie</option>
                  <option v-for="categorie in categories" :value="categorie.id" :key="categorie.id">{{ categorie.name }}</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select v-model="productform.status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="1">Actif</option>
                  <option value="0">Inactif</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea v-model="productform.description" rows="5"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Slug (optionnel)
                </label>
                <input v-model="productform.slug"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="auto-généré si vide" />
              </div>

              <div v-if="error" class="bg-red-50 text-red-800 p-3 rounded-lg text-sm">
                {{ error }}
              </div>

              <div class="flex space-x-3 pt-4">
                <button type="button" @click="resetForm"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  Annuler
                </button>
                <button type="submit"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 bg-black text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed">
                  <span v-if="saving" class="flex items-center justify-center">
                    <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    {{ editingproduct ? 'Enregistrement...' : 'Ajout...' }}
                  </span>
                  <span v-else>
                    {{ editingproduct ? 'Enregistrer' : 'Ajouter' }}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Delete Modal -->
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click.self="showDeleteModal = null">
        <div class="bg-white rounded-xl shadow-lg w-full max-w-sm">
          <div class="p-6 text-center">
            <svg class="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              Confirmer la suppression
            </h3>
            <p class="text-gray-600 mb-6">
              Supprimer "{{ showDeleteModal.name }}" ?
            </p>
            <div v-if="deleteError" class="bg-red-50 text-red-800 p-3 rounded-lg text-sm mb-4">
              {{ deleteError }}
            </div>
            <div class="flex space-x-3">
              <button @click="showDeleteModal = null"
                :disabled="deleting"
                class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                Annuler
              </button>
              <button @click="handleDelete"
                :disabled="deleting"
                class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="deleting" class="flex items-center justify-center">
                  <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Suppression...
                </span>
                <span v-else>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ProductModelsPage',

  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      showModal: false,
      showDeleteModal: null,
      editingproduct: null,
      searchTerm: '',
      error: null,
      deleteError: null,

      productform: {
        id: null,
        name: '',
        brand_id: '',
        categorie_id: '',
        description: '',
        status: '1',
        slug: ''
      }
    }
  },

  computed: {
    // Utilisez les modèles de produit admin du store
    products() {
      return this.$store.state.admin.productModels
    },
    
    // Ou utilisez le getter
    adminProductModels() {
      return this.$store.getters.adminProductModels
    },
    
    // Marques pour le select
    brands() {
      return this.$store.state.admin.brands
    },
    
    // Catégories pour le select
    categories() {
      return this.$store.state.admin.categories
    },
    
    filteredproducts() {
      const productsList = this.products
      if (!productsList) return []
      
      return productsList.filter(product => 
        product.name && product.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(this.searchTerm.toLowerCase()))
      )
    }
  },

  async mounted() {
    await this.loadData()
  },

  methods: {
     generateSlug(name) {
  return name
    .toLowerCase()
    // Autorise lettres (avec accents), chiffres et tirets
    .replace(/[^a-z0-9àâäéèêëîïôöùûüç-]+/gi, '-')
    // Regroupe tous les tirets multiples en un seul
    .replace(/-+/g, '-')
    // Supprime les tirets au début et à la fin
    .replace(/^-+|-+$/g, '');
}
,
   handleNameChange(event) {
      const name = event.target.value
      this.productform.name = name
      
        this.productform.slug = this.generateSlug(name)
     
    },
    async loadData() {
      try {
        this.loading = true
        // Chargez toutes les données admin depuis le store
        await this.$store.dispatch('fetchAdminData')
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        this.error = 'Impossible de charger les données. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },

    getBrandName(brandId) {
      if (!brandId || !this.brands) return ''
      const brand = this.brands.find(b => b.id == brandId)
      return brand ? brand.name : ''
    },

    getStatusColor(status) {
      switch (status) {
        case 'approved': return 'bg-green-100 text-green-800'
        case 'rejected': return 'bg-red-100 text-red-800'
        case 'received': return 'bg-blue-100 text-blue-800'
        case 'pending': return 'bg-yellow-100 text-yellow-800'
        default: return status == 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'En attente',
        received: 'Reçu',
        approved: 'Approuvé',
        rejected: 'Rejeté',
        1: 'Actif',
        0: 'Inactif'
      }
      return labels[status] || status
    },

    openAddModal() {
      this.editingproduct = null
      this.resetFormData()
      this.showModal = true
      this.error = null
    },

    resetFormData() {
      this.productform = {
        id: null,
        name: '',
        brand_id: '',
        categorie_id: '',
        description: '',
        status: '1',
        slug: ''
      }
    },

    resetForm() {
      this.showModal = false
      this.editingproduct = null
      this.resetFormData()
      this.error = null
    },

    async handleSubmit() {
      try {
        this.saving = true
        this.error = null
        
        // Validation basique
        if (!this.productform.name) {
          throw new Error('Le nom du modèle est obligatoire')
        }
        
        if (!this.productform.brand_id) {
          throw new Error('La marque est obligatoire')
        }
        
        if (!this.productform.categorie_id) {
          throw new Error('La catégorie est obligatoire')
        }
        if (!this.productform.slug) {
          this.productform.slug = this.generateSlug(this.productform.name)
        }

        // Appelez l'action du store pour sauvegarder le modèle de produit
        const savedProductModel = await this.$store.dispatch('saveProductModel', this.productform)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: `Modèle ${this.editingproduct ? 'modifié' : 'créé'} avec succès`,
          type: 'success'
        })
        
        this.resetForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
        this.error = error.response?.data?.message || error.message || 'Une erreur est survenue lors de la sauvegarde.'
      } finally {
        this.saving = false
      }
    },

    handleEdit(product) {
      this.editingproduct = product
      this.productform = {
        id: product.id,
        name: product.name || '',
        brand_id: product.brand_id || '',
        categorie_id: product.categorie_id || '',
        description: product.description || '',
        status: product.status !== undefined ? product.status : '1',
        slug: product.slug || ''
      }
      this.showModal = true
      this.error = null
    },
notify(title,message,type){
          this.$emit('notify',{title:title,message:message,type:type})
    },
    async handleDelete() {
      try {
        this.deleting = true
        this.deleteError = null
        
        // Appelez l'action de suppression du store
        await this.$store.dispatch('deleteProductModel', this.showDeleteModal.id)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: 'Modèle supprimé avec succès',
          type: 'success'
        })
        
        this.showDeleteModal = null
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        this.deleteError = error.response?.data?.message || 'Une erreur est survenue lors de la suppression.'
      } finally {
        this.deleting = false
      }
    },

    updateStatus(id, newStatus) {
      this.products = this.products.map(s =>
        s.id === id ? {
          ...s,
          status: newStatus
        } : s
      )
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

input,
select {
  transition: all 0.2s ease;
}

input:focus,
select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.fixed {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.bg-white {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bg-white:hover {
  transform: translateY(-2px);
}
</style>